import { API_URL } from "./urls.js";
import { getAccessToken } from "./auth.js";
import { redact } from "./credentials.js";
import { withNextStep } from "./guidance.js";
import { mcpTimeoutMs, type AskResult } from "./ask.js";
import { CARRIER_VERSION } from "../../version.js";
import type { RestMethod } from "./domains.js";

export interface RestCallOptions {
  /** Recovery steps appended when the endpoint itself reports an error. */
  errorHints?: string[];
  /** Second-call token from a prior 202, sent as X-Confirm-Token. */
  confirmToken?: string;
}

const DEFAULT_ERROR_HINTS = [
  "Re-check the arguments: carrier <domain> <command> --help",
  "Or open Claude and ask there with full tool routing",
];

/**
 * Bearer for `apps/api`, which is Clerk-only.
 *
 * `resolveCliToken()` from status.ts is deliberately NOT reused here: it falls
 * back to ESIMVAULT_API_TOKEN / CARRIER_OCS_API_TOKEN, and `apps/api/src/auth.ts`
 * accepts none of those — an OCS token would come back as a bare 401 that reads
 * like an expired login rather than the wrong credential entirely.
 */
function resolveApiKey(): string | null {
  return (
    process.env.CARRIER_API_KEY?.trim() ||
    process.env.CARRIER_ORG_API_KEY?.trim() ||
    null
  );
}

function ocsTokenOnly(): boolean {
  return Boolean(
    process.env.ESIMVAULT_API_TOKEN?.trim() || process.env.CARRIER_OCS_API_TOKEN?.trim(),
  );
}

/** Fill `{name}` placeholders from args; return the rest for body or query. */
function applyPath(
  path: string,
  args: Record<string, unknown>,
): { path: string; rest: Record<string, unknown>; missing: string[] } {
  const rest = { ...args };
  const missing: string[] = [];
  const filled = path.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = rest[name];
    if (value === undefined || value === null || value === "") {
      missing.push(name);
      return `{${name}}`;
    }
    delete rest[name];
    return encodeURIComponent(String(value));
  });
  return { path: filled, rest, missing };
}

function queryString(args: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(args)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function bodyText(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    const parsed = JSON.parse(trimmed) as { error?: unknown; message?: unknown };
    if (typeof parsed.error === "string") return parsed.error;
    if (typeof parsed.message === "string") return parsed.message;
    return trimmed;
  } catch {
    return trimmed;
  }
}

/**
 * Call one Carrier REST endpoint, returning the same shape as `callMcpTool` so
 * `runCapability` stays a single branch.
 *
 * The 202 + `confirm_token` handshake in `apps/api/src/confirm-token.ts` is
 * surfaced, not followed: the point of that protocol is that a human re-issues
 * the call, so auto-retrying it here would defeat the gate.
 */
export async function callRestEndpoint(
  method: RestMethod,
  path: string,
  args: Record<string, unknown>,
  opts: RestCallOptions = {},
): Promise<AskResult> {
  const token = resolveApiKey() ?? (await getAccessToken());
  if (!token) {
    return {
      ok: false,
      text: withNextStep(
        ocsTokenOnly()
          ? "Not signed in. An eSIMVault/OCS token is set, but the Carrier API only accepts Carrier credentials."
          : "Not signed in.",
        [
          "Sign in from this terminal: carrier login",
          "No account yet? carrier open signup",
          "Headless/CI instead: export CARRIER_API_KEY=ak_… from Console → Settings → API Keys",
        ],
      ),
    };
  }

  const { path: resolvedPath, rest, missing } = applyPath(path, args);
  if (missing.length) {
    return {
      ok: false,
      text: withNextStep(`Cannot build ${method} ${path}.`, [
        `Missing path value(s): ${missing.join(", ")}`,
        "See every flag: carrier <domain> <command> --help",
      ]),
    };
  }

  const sendsBody = method === "POST" || method === "PUT" || method === "PATCH";
  const url = `${API_URL}${resolvedPath}${sendsBody ? "" : queryString(rest)}`;

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    "User-Agent": `carrier-cli/${CARRIER_VERSION}`,
  };
  if (sendsBody) headers["Content-Type"] = "application/json";
  if (opts.confirmToken) headers["X-Confirm-Token"] = opts.confirmToken;

  const timeoutMs = mcpTimeoutMs();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      method,
      headers,
      signal: controller.signal,
      body: sendsBody ? JSON.stringify(rest) : undefined,
    });
    const raw = await res.text();

    // Two-step confirm. The token is single-use and expires in ~2 minutes.
    if (res.status === 202) {
      let confirmToken: string | undefined;
      let expires: number | undefined;
      let message = "";
      try {
        const parsed = JSON.parse(raw) as {
          confirm_token?: string;
          expires_in_seconds?: number;
          message?: string;
        };
        confirmToken = parsed.confirm_token;
        expires = parsed.expires_in_seconds;
        message = parsed.message ?? "";
      } catch {
        // Not JSON — fall through to the generic hint below.
      }
      return {
        ok: false,
        text: withNextStep(
          message || `${method} ${path} needs an explicit confirmation.`,
          confirmToken
            ? [
                `Re-run the same command with: --confirm-token ${confirmToken}`,
                `The token is single-use and expires in ${expires ?? 120}s.`,
              ]
            : ["The server asked for confirmation but returned no token — retry the command."],
        ),
      };
    }

    if (res.status === 401 || res.status === 403) {
      return {
        ok: false,
        text: withNextStep(
          `Carrier API returned ${res.status} (auth rejected).`,
          [
            "Confirm CARRIER_API_KEY is a valid org key (ak_…) from app.carrier.llc",
            "Or sign in from this terminal: carrier login",
            ...(res.status === 403 ? ["A 403 here usually means the key lacks write scope."] : []),
          ],
        ),
      };
    }

    if (!res.ok) {
      return {
        ok: false,
        text: withNextStep(
          redact(`${method} ${path} failed (${res.status}). ${bodyText(raw).slice(0, 500)}`),
          opts.errorHints ?? DEFAULT_ERROR_HINTS,
        ),
      };
    }

    // 2xx. Hand the body back untouched so --json stays pipeable; whatever the
    // endpoint chose to return, including a one-time secret, is the caller's.
    return { ok: true, text: raw.trim() || JSON.stringify({ status: res.status }) };
  } catch (e: unknown) {
    const aborted = e instanceof Error && (e.name === "AbortError" || e.name === "TimeoutError");
    if (aborted) {
      const secs = Math.round(timeoutMs / 1000);
      return {
        ok: false,
        text: withNextStep(`${method} ${path} timed out after ${secs}s (no response).`, [
          `Raise the deadline for one slow call: CARRIER_MCP_TIMEOUT_MS=${timeoutMs * 2} carrier …`,
          "Confirm the service is up: check network access to https://api.carrier.llc",
        ]),
      };
    }
    const msg = e instanceof Error ? e.message : String(e);
    return {
      ok: false,
      text: withNextStep(redact(`Could not reach the Carrier API: ${msg}`), [
        "Verify outbound HTTPS to api.carrier.llc",
        "Use the interactive Claude path while offline from this machine",
      ]),
    };
  } finally {
    clearTimeout(timer);
  }
}
